import { useRef, useState, useEffect } from 'react';
import NavIcon from './NavIcon.jsx';
import { createPortal } from 'react-dom';

// Bottom navigation with direct-manipulation customization:
//  • tap            → switch tab
//  • horizontal swipe (quick) → native scroll of the overflowing bar
//  • long-press     → pick a tab up, then drag horizontally to reorder
//  • drag up/out of the bar → drop = hide the tab (same as the settings toggle)
const LONG_PRESS_MS = 300;
const MOVE_SLOP = 8;    // movement before the long-press fires cancels the pickup
const REMOVE_GAP = 24;  // px above the bar's top edge that marks the "remove" zone

export default function BottomNav({ items, order, hidden, active, chatUnread, t, onSelect, onReorder, onHide }) {
  const scrollerRef = useRef(null);
  const rootRef = useRef(null);
  const itemEls = useRef({});
  const press = useRef(null);       // { id, node, startX, startY, pointerId }
  const timer = useRef(null);
  const mode = useRef('idle');      // idle | pending | drag | cancelled
  const suppressClick = useRef(false);
  const [drag, setDrag] = useState(null); // { id, x, y, removing, target }

  // Publish the bar's real height so page content can pad for it (single row,
  // scrolls horizontally when the tabs overflow).
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const apply = () => document.documentElement.style.setProperty('--bottomnav-h', el.offsetHeight + 'px');
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Desktop: a mouse wheel has no horizontal axis, so translate vertical wheel
  // into horizontal scroll of the bar (only when it actually overflows).
  // Non-passive listener so preventDefault stops the page from scrolling too.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth || e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // While a tab is being dragged (long-press → reorder), stop the bar from
  // scrolling — otherwise a horizontal drag scrolls the bar instead of moving
  // the tab. Non-passive so preventDefault actually cancels the touch scroll.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const block = (e) => { if (mode.current === 'drag') e.preventDefault(); };
    el.addEventListener('touchmove', block, { passive: false });
    return () => el.removeEventListener('touchmove', block);
  }, []);

  const label = (it) => { if (!it) return ''; const k = 'nav_' + it.id; const v = t(k); return v && v !== k ? v : it.label; };
  const visible = order.map(id => items.find(i => i.id === id)).filter(Boolean).filter(i => !hidden.includes(i.id));

  const computeTarget = (x) => {
    for (let k = 0; k < visible.length; k++) {
      const el = itemEls.current[visible[k].id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (x < r.left + r.width / 2) return k;
    }
    return visible.length;
  };

  const endDrag = (commit) => {
    const d = drag;
    setDrag(null);
    mode.current = 'idle';
    if (!d || !commit) return;
    suppressClick.current = true;
    setTimeout(() => { suppressClick.current = false; }, 400);
    if (d.removing) {
      if (visible.length > 1) onHide(d.id);   // never empty the bar
      return;
    }
    const ids = visible.map(v => v.id);
    const from = ids.indexOf(d.id);
    if (from < 0) return;
    let to = d.target;
    ids.splice(from, 1);
    if (to > from) to -= 1;
    to = Math.max(0, Math.min(ids.length, to));
    ids.splice(to, 0, d.id);
    if (ids.some((id, i) => id !== visible[i].id)) onReorder(ids);
  };

  const onDown = (e, id) => {
    if (e.button != null && e.button !== 0) return;
    const node = e.currentTarget;
    press.current = { id, node, startX: e.clientX, startY: e.clientY, pointerId: e.pointerId };
    mode.current = 'pending';
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (mode.current !== 'pending' || !press.current) return;
      mode.current = 'drag';
      try { node.setPointerCapture(press.current.pointerId); } catch {}
      try { navigator.vibrate?.(15); } catch {}
      setDrag({ id, x: press.current.startX, y: press.current.startY, removing: false, target: visible.findIndex(v => v.id === id) });
    }, LONG_PRESS_MS);
  };

  const onMove = (e) => {
    const p = press.current;
    if (!p) return;
    const dx = e.clientX - p.startX, dy = e.clientY - p.startY;
    if (mode.current === 'pending') {
      if (Math.abs(dx) > MOVE_SLOP || Math.abs(dy) > MOVE_SLOP) { clearTimeout(timer.current); mode.current = 'cancelled'; }
      return;
    }
    if (mode.current !== 'drag') return;
    e.preventDefault();
    const top = scrollerRef.current ? scrollerRef.current.getBoundingClientRect().top : Infinity;
    const removing = e.clientY < top - REMOVE_GAP;
    setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, removing, target: removing ? d.target : computeTarget(e.clientX) } : d);
  };

  const onUp = () => {
    clearTimeout(timer.current);
    const wasDrag = mode.current === 'drag';
    press.current = null;
    if (wasDrag) endDrag(true);
    else mode.current = 'idle';
  };

  const onCancel = () => {
    clearTimeout(timer.current);
    press.current = null;
    if (mode.current === 'drag') endDrag(false);
    else mode.current = 'idle';
  };

  const onClickItem = (e, id) => {
    if (suppressClick.current) { suppressClick.current = false; e.preventDefault(); return; }
    if (mode.current === 'drag') { e.preventDefault(); return; }
    onSelect(id);
  };

  const Line = () => <div style={{ width: '2px', alignSelf: 'stretch', background: 'var(--app-accent,#c9a24b)', borderRadius: '1px', margin: '8px 0' }} />;

  return createPortal(
    <div ref={rootRef} style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--app-sidebar,#0a0a0d)', borderTop: '1px solid #1a1a20', display: 'flex', justifyContent: 'center', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4px)' }}>
      <style>{`.bn-scroller{scrollbar-width:none;-ms-overflow-style:none;overscroll-behavior-x:contain}.bn-scroller::-webkit-scrollbar{display:none}.bn-scroller,.bn-scroller *{-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;-webkit-tap-highlight-color:transparent}`}</style>
      <div ref={scrollerRef} className="bn-scroller" style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'safe center', overflowX: 'auto', width: '100%', maxWidth: '900px', touchAction: drag ? 'none' : 'pan-x', WebkitOverflowScrolling: 'touch' }}>
        {visible.map((item, idx) => {
          const isActive = active === item.id;
          const isDragged = drag && drag.id === item.id;
          const lineBefore = drag && !drag.removing && drag.target === idx && drag.id !== item.id;
          const lineAfterLast = idx === visible.length - 1 && drag && !drag.removing && drag.target === visible.length && drag.id !== item.id;
          return (
            <div key={item.id} style={{ display: 'flex' }}>
              {lineBefore && <Line />}
              <button ref={el => { if (el) itemEls.current[item.id] = el; }}
                className="tap"
                onPointerDown={(e) => onDown(e, item.id)} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onCancel}
                onClick={(e) => onClickItem(e, item.id)}
                aria-current={isActive ? 'page' : undefined}
                aria-label={label(item)}
                onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(`${window.location.origin}/#${item.id}`, '_blank'); } }}
                style={{ flexShrink: 0, minWidth: '72px', padding: '12px 8px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                         display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                         opacity: isDragged ? 0.25 : 1, touchAction: drag ? 'none' : 'pan-x' }}>
                <span style={{ fontSize: '18px', lineHeight: 1, height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: isActive ? 1 : 0.85, position: 'relative' }}>
                  <NavIcon id={item.id} emoji={item.icon} size={18} />
                  {item.id === 'chat' && chatUnread > 0 && (
                    <span style={{ position: 'absolute', top: '-4px', right: '-8px', background: 'var(--app-accent,#c9a24b)', color: '#0d0d10', borderRadius: '9px', minWidth: '16px', height: '16px', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>
                      {chatUnread > 99 ? '99+' : chatUnread}
                    </span>
                  )}
                </span>
                <span style={{ whiteSpace: 'nowrap', fontSize: '10px', fontWeight: 600, color: isActive ? 'var(--app-accent,#c9a24b)' : '#7a7a82' }}>
                  {label(item)}
                </span>
                {isActive && <div style={{ width: '20px', height: '2px', background: 'var(--app-accent,#c9a24b)', borderRadius: '1px' }} />}
              </button>
              {lineAfterLast && <Line />}
            </div>
          );
        })}
      </div>

      {drag && (
        <div style={{ position: 'fixed', left: drag.x, top: drag.y, transform: 'translate(-50%,-50%)', pointerEvents: 'none', zIndex: 300,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '8px 12px', borderRadius: '12px',
                      background: drag.removing ? 'rgba(239,68,68,.18)' : 'var(--app-sidebar,#0a0a0d)',
                      border: `1px solid ${drag.removing ? 'var(--app-bad,#ef4444)' : 'var(--app-accent,#c9a24b)'}`, boxShadow: '0 8px 24px rgba(0,0,0,.5)' }}>
          <span style={{ fontSize: '20px' }}>{drag.removing ? '🗑' : items.find(i => i.id === drag.id)?.icon}</span>
          <span style={{ fontSize: '10px', whiteSpace: 'nowrap', color: drag.removing ? '#ef4444' : 'var(--app-text,#ece6d6)' }}>
            {drag.removing ? t('nav_remove_hint') : label(items.find(i => i.id === drag.id))}
          </span>
        </div>
      )}
    </div>,
    document.body
  );
}
